/**
 * RoamerDataFusionEngine.ts — Engine #4: the Roamer Data Fusion Engine™.
 *
 * The centralized data hub for WorkRoamer. It is the ONLY service that talks to
 * external data providers. Every other engine subscribes to it instead of
 * hitting third-party APIs:
 *
 *   External APIs → Roamer Data Fusion Engine → Location / Intelligence /
 *     Mission / Roamer Score / future engines
 *
 * Per request it: resolves the provider fallback chain, calls each provider
 * with timeout + retry, validates → de-duplicates → confidence-ranks the
 * results, caches them, records provider health, and publishes an event. If
 * every provider fails it serves stale cache; if there is none, a graceful
 * empty state — the user never sees a broken card.
 */

import type { RoamerLocation } from '@/services/RoamerLocationEngine';
import type { DomainResultMap, FusedEntity, FusionDomain } from './models';
import { FusionCache } from './cache/FusionCache';
import { ProviderHealthRegistry } from './health/ProviderHealth';
import { FusionEventBus, fusionEventBus, domainToEvent } from './events/FusionEventBus';
import {
  FusionProviderRegistry,
  fusionProviderRegistry,
} from './providers/ProviderRegistry';
import type { FusionQuery, ProviderMeta } from './providers/types';
import {
  aggregateConfidence,
  deduplicateRecords,
  rankByConfidence,
  reageRecords,
  validateRecords,
} from './quality/qualityControl';
import {
  ProviderUnavailableError,
  RateLimitError,
  withRetry,
  withTimeout,
} from './errors';

export type FusionResultSource = 'provider' | 'cache' | 'stale-cache' | 'empty';

/** What every engine/UI receives — normalized records plus full provenance. */
export interface FusionResult<T extends FusedEntity> {
  domain: FusionDomain;
  records: T[];
  source: FusionResultSource;
  providerId: string | null;
  fetchedAt: number;
  /** true when serving stale cache or an empty state (a provider failure occurred). */
  degraded: boolean;
  /** Aggregate confidence 0–100. */
  confidence: number;
}

export interface FusionFetchOptions {
  location?: RoamerLocation;
  limit?: number;
  now?: number;
  params?: Record<string, unknown>;
  /** Force a provider round-trip even if fresh cache exists. */
  bypassCache?: boolean;
}

export interface EngineConfig {
  timeoutMs: number;
  retries: number;
  backoffMs: number;
}

const DEFAULT_CONFIG: EngineConfig = { timeoutMs: 8_000, retries: 1, backoffMs: 200 };

export class RoamerDataFusionEngine {
  private cache: FusionCache;
  private health: ProviderHealthRegistry;

  constructor(
    private registry: FusionProviderRegistry = fusionProviderRegistry,
    private events: FusionEventBus = fusionEventBus,
    private config: EngineConfig = DEFAULT_CONFIG,
    cache?: FusionCache,
  ) {
    this.cache = cache ?? new FusionCache();
    this.health = new ProviderHealthRegistry();
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Fetch normalized data for a domain. Never throws for provider issues — it
   * degrades to cache, then to an empty result.
   */
  async fetch<D extends FusionDomain>(
    domain: D,
    location: RoamerLocation,
    opts: FusionFetchOptions = {},
  ): Promise<FusionResult<DomainResultMap[D]>> {
    const now = opts.now ?? Date.now();
    const key = this.cacheKey(domain, location, opts);

    if (!opts.bypassCache) {
      const fresh = this.cache.getFresh<FusionResult<DomainResultMap[D]>>(key, now);
      if (fresh) return this.cloneResult(fresh, 'cache', now);
    }

    const chain = this.registry.chain(domain);
    if (chain.length === 0) {
      // No provider at all — still never throw at the boundary.
      return this.emptyResult(domain, now);
    }

    const query: FusionQuery = {
      location,
      domain,
      limit: opts.limit,
      now,
      params: opts.params,
    };

    // Walk the fallback chain: primary → fallback → … until one yields data.
    for (const provider of chain) {
      // Wall-clock, independent of the injected `now`, so latency is real.
      const startedWallClock = Date.now();
      try {
        const raw = await withRetry(
          () => withTimeout(provider.fetch(query), this.config.timeoutMs, provider.meta.id),
          { retries: this.config.retries, backoffMs: this.config.backoffMs },
        );
        this.health.recordSuccess(provider.meta.id, Math.max(0, Date.now() - startedWallClock), now);

        const clean = this.processRecords(domain, raw as DomainResultMap[D][], provider.meta, now);
        // A provider that returns zero valid records is a soft failure — try the next.
        if (clean.length === 0) continue;

        const result: FusionResult<DomainResultMap[D]> = {
          domain,
          records: clean,
          source: 'provider',
          providerId: provider.meta.id,
          fetchedAt: now,
          degraded: false,
          confidence: aggregateConfidence(clean),
        };
        this.cache.set(key, domain, result, now);
        this.publish(domain, provider.meta.id, clean.length, now);
        return this.cloneResult(result, 'provider', now);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        this.health.recordFailure(provider.meta.id, message, now);
        // Rate limits and every other error simply advance the chain.
        if (err instanceof RateLimitError) continue;
        continue;
      }
    }

    // Every provider failed → serve stale cache if we have any.
    const stale = this.cache.peek<FusionResult<DomainResultMap[D]>>(key, now);
    if (stale) {
      this.publish(domain, stale.value.providerId, stale.value.records.length, now);
      return this.cloneResult(stale.value, 'stale-cache', now);
    }

    // Nothing anywhere → graceful empty state.
    this.publish(domain, null, 0, now);
    return this.emptyResult(domain, now);
  }

  /** Which providers back the engine and their live/placeholder status. */
  getProviderStatus(): ProviderMeta[] {
    return this.registry.listProviders();
  }

  /** Provider health snapshot (for diagnostics / future engines). */
  getProviderHealth() {
    return this.health.list();
  }

  /** Register a real (or fallback) provider at runtime. */
  registerProvider(provider: Parameters<FusionProviderRegistry['register']>[0]): void {
    this.registry.register(provider);
  }

  /** Subscribe to fusion events (engines use this instead of polling). */
  on(name: Parameters<FusionEventBus['on']>[0], listener: Parameters<FusionEventBus['on']>[1]): () => void {
    return this.events.on(name, listener);
  }

  onAny(listener: Parameters<FusionEventBus['onAny']>[0]): () => void {
    return this.events.onAny(listener);
  }

  /** Scoped cache invalidation for one location only (never touches others). */
  invalidateLocation(locationId: string): number {
    return this.cache.invalidateLocation(locationId);
  }

  clearCache(): void {
    this.cache.clear();
  }

  // ── Internals ────────────────────────────────────────────────────────────────

  private processRecords<D extends FusionDomain>(
    domain: D,
    raw: DomainResultMap[D][],
    meta: ProviderMeta,
    now: number,
  ): DomainResultMap[D][] {
    const valid = validateRecords(domain, raw);
    const deduped = deduplicateRecords(valid);
    return rankByConfidence(deduped, {
      providerId: meta.id,
      baseConfidence: meta.baseConfidence,
      lastUpdated: now,
      now,
      ttlMs: this.cache.getTtl(domain),
    });
  }

  private cacheKey(domain: FusionDomain, location: RoamerLocation, opts: FusionFetchOptions): string {
    const paramSig = opts.params ? JSON.stringify(opts.params) : '';
    return `${domain}|${location.locationId}|${opts.limit ?? ''}|${paramSig}`;
  }

  private emptyResult<D extends FusionDomain>(domain: D, now: number): FusionResult<DomainResultMap[D]> {
    return {
      domain,
      records: [],
      source: 'empty',
      providerId: null,
      fetchedAt: now,
      degraded: true,
      confidence: 0,
    };
  }

  private publish(domain: FusionDomain, providerId: string | null, recordCount: number, now: number): void {
    this.events.emit({ name: domainToEvent(domain), domain, providerId, at: now, recordCount });
  }

  /**
   * Deep-clone so cached results can never be mutated by a caller, and re-age
   * cached records at READ time so every confidence envelope reflects the
   * data's real age (stale-cache records are explicitly marked `stale`).
   */
  private cloneResult<T extends FusedEntity>(
    result: FusionResult<T>,
    source: FusionResultSource,
    now: number,
  ): FusionResult<T> {
    let records = result.records.map((r) => structuredClone(r));
    let confidence = result.confidence;
    if (source === 'cache' || source === 'stale-cache') {
      records = reageRecords(records, now, this.cache.getTtl(result.domain));
      confidence = aggregateConfidence(records);
    }
    return {
      ...result,
      source,
      degraded: source === 'stale-cache' || source === 'empty' ? true : result.degraded,
      confidence,
      records,
    };
  }
}

/** App-wide singleton — the central data hub every engine consumes. */
export const roamerDataFusionEngine = new RoamerDataFusionEngine();
